/**
 * CR042 Addendum: Admin → Website Runs (Gift Deals Scan) page
 * CR112: Renamed from "POC Runs" to "Website Runs"
 * CR114: Removed "Select Merchants" CTA - merchant selection now in Test Settings
 * CR127: Unified run status handling with shared hook
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
// Dialog imports removed - CR128: Eye modal removed, Open page is canonical
// Progress import removed - only used in Eye modal
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Play, XCircle, Loader2, ExternalLink, RefreshCw, Gift, Settings, ImageIcon, Calendar } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Link } from "react-router-dom";
import { DealGiftCard } from "@/components/deals/DealGiftCard";
import { DealTypeFilter } from "@/components/deals/DealTypeFilter";
import { type Deal, type DealCategory } from "@/lib/types/deals";
// CR128: useRunSummary and getEffectiveMerchantStatus no longer needed here - Eye modal removed
import { STATUS_COLORS } from "@/lib/poc-pipeline/runStatusUtils";

interface PocRun {
  id: string;
  run_type: string;
  run_mode: string | null;
  status: string;
  merchant_limit_used: number;
  prompt_pack_id: string | null;
  scan_model_used: string;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  created_at: string;
  // New fields for POC Run Units architecture
  origin_type: string | null;
  origin_id: string | null;
  parent_run_id: string | null;
  units_total: number;
  units_succeeded: number;
  units_failed: number;
  staged_count: number;
  // CR161: Scheduler context (joined from cr075_batch_runs)
  schedule_run_id?: string | null;
  schedule_id?: string | null;
}

// CR161: Origin filter type
type OriginFilter = "all" | "manual" | "scheduler";

interface PocRunMerchant {
  id: string;
  poc_run_id: string;
  merchant_id: string;
  merchant_name: string | null;
  source_url: string | null;
  status: string;
  pages_scanned: number;
  candidates_found: number;
  deals_created: number;
  error_message: string | null;
  resolved_sources: any;
  resolve_notes: string | null;
  text_source_type: string | null;
  text_char_count: number | null;
}

interface PromptPack {
  id: string;
  name: string;
  version: number;
}

type RewardForm = "GIFT_CARD" | "CASH" | "POINTS" | "OTHER";

interface PocDeal {
  id: string;
  run_id: string;
  merchant_id: string;
  merchant_name: string | null;
  source_id: string;
  source_url: string | null;  // The deal's direct URL (e.g., Fig's site)
  discovery_url: string | null; // Where the deal was discovered (e.g., Walmart partner offers page)
  screenshot_url: string | null;
  gift_card_brand: string;
  deal_type: string;
  buy_amount: number | null;
  buy_quantity: number | null;
  reward_value: number | null;
  reward_unit: string;
  reward_form: RewardForm | null;  // GIFT_CARD | CASH | POINTS | OTHER
  terms_text: string | null;
  evidence_verbatim_text: string | null;
  evidence_page: number | null;
  confidence: number;
  region: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  // New fields from POC pipeline
  partner_merchant: string | null;
  max_reward_cad: number | null;
  eligibility_summary: string | null;
  // CR058: Direct discount/spend fields from database
  discount_percent: number | null;
  qualifying_spend_cad: number | null;
  // CR064: Separate deal and terms URLs
  deal_url: string | null;   // Direct link to view/claim the offer
  terms_url: string | null;  // Link to terms and conditions
}

// Map POC deal types to Deal category
// CR335: Map POC deal types to canonical categories only
function mapPocDealType(pocType: string): { category: DealCategory; dealType: Deal["deal_type"] } {
  switch (pocType?.toUpperCase()) {
    case "BONUS":
      return { category: "BONUS", dealType: "bonus" };
    case "DISCOUNT":
      return { category: "DISCOUNT", dealType: "discount" };
    case "POINTS":
      return { category: "POINTS", dealType: "points" };
    case "IN_KIND":
      return { category: "IN_KIND", dealType: "in_kind" };
    default:
      return { category: "UNCLASSIFIED", dealType: "unclassified" };
  }
}

// Map POC deal to UI Deal format
function mapPocDealToUIDeal(pocDeal: PocDeal): Deal {
  const { category, dealType } = mapPocDealType(pocDeal.deal_type);
  const isPercent = pocDeal.reward_unit === "PERCENT";
  const isPoints = pocDeal.reward_unit === "POINTS";
  
  // Use partner_merchant as display merchant if available (e.g., "Walmart Travel")
  const displayMerchant = pocDeal.partner_merchant || pocDeal.merchant_name || pocDeal.merchant_id || "Unknown Merchant";
  
  // Helper: Extract minimum spend from eligibility summary (e.g., "$1,000 - $4,999" -> 1000)
  const extractMinSpend = (text: string | null): number | null => {
    if (!text) return null;
    const spendMatch = text.match(/\$([0-9,]+)/);
    return spendMatch ? parseFloat(spendMatch[1].replace(/,/g, '')) : null;
  };
  
  // CR058: Use direct discount_percent and qualifying_spend_cad from database first
  // Fall back to legacy reward_unit/reward_value logic for older deals
  const discountPercentValue = pocDeal.discount_percent ?? (isPercent ? pocDeal.reward_value : null);
  const qualifyingSpendValue = pocDeal.qualifying_spend_cad ?? pocDeal.buy_amount ?? extractMinSpend(pocDeal.eligibility_summary);
  
  // Compute effective spend amount
  const effectiveSpend = qualifyingSpendValue;
  
  // Compute dollar value for percentage deals
  let computedDollarValue: number | null = null;
  if (discountPercentValue && effectiveSpend) {
    computedDollarValue = effectiveSpend * (discountPercentValue / 100);
  } else if (isPercent && pocDeal.reward_value && effectiveSpend) {
    computedDollarValue = effectiveSpend * (pocDeal.reward_value / 100);
  }
  
  // Build headline mechanic - CR059: Use dollar headlines for tiered discounts, % for variable spend
  let headline = "";
  let headlineMode: "DOLLAR_ON_SPEND" | "PERCENT_VARIABLE" | "FIXED_VALUE" | "DEFAULT" = "DEFAULT";
  
  // Determine reward form suffix for clarity
  const rewardFormSuffix = pocDeal.reward_form === "GIFT_CARD" 
    ? " in gift cards" 
    : pocDeal.reward_form === "POINTS" 
      ? " in points" 
      : "";

  // Helper to format currency values
  const formatValue = (value: number) => value >= 10 
    ? `$${Math.round(value).toLocaleString()}` 
    : `$${value.toFixed(2)}`;
  
  const formatSpend = (amount: number) => `$${amount.toLocaleString()}`;

  if (category === "DISCOUNT") {
    // CR059: DISCOUNT headline logic
    // Rule: If spend is known → dollar headline; If variable spend → percent headline
    // CR058: Use discountPercentValue from database first
    if (effectiveSpend && effectiveSpend > 0 && discountPercentValue) {
      // Tiered DISCOUNT: "Get $5,000 off when spending $50,000"
      const dollarOff = computedDollarValue ?? effectiveSpend * (discountPercentValue / 100);
      headline = `Get ${formatValue(dollarOff)} off when spending ${formatSpend(effectiveSpend)}`;
      headlineMode = "DOLLAR_ON_SPEND";
      computedDollarValue = dollarOff;
    } else if (discountPercentValue) {
      // Variable spend DISCOUNT: "Get 5% back in Walmart gift cards"
      const brandSuffix = pocDeal.gift_card_brand ? ` in ${pocDeal.gift_card_brand} gift cards` : "";
      headline = `Get ${discountPercentValue}% back${rewardFormSuffix || brandSuffix}`;
      headlineMode = "PERCENT_VARIABLE";
    } else if (pocDeal.reward_value && !isPercent) {
      // Fixed dollar off
      headline = `Save ${formatValue(pocDeal.reward_value)} on ${pocDeal.gift_card_brand}`;
      headlineMode = "FIXED_VALUE";
    } else {
      headline = pocDeal.evidence_verbatim_text || `${pocDeal.gift_card_brand} discount deal`;
    }
  } else if (category === "BONUS") {
    // Handle BONUS deals - check for "up to $X" first (max_reward_cad)
    if (pocDeal.max_reward_cad && !pocDeal.reward_value) {
      headline = `Get up to $${pocDeal.max_reward_cad}${rewardFormSuffix || ` in ${pocDeal.gift_card_brand} gift cards`}`;
      if (pocDeal.eligibility_summary) {
        headline += ` • ${pocDeal.eligibility_summary}`;
      }
    } else if (isPercent && pocDeal.reward_value && effectiveSpend) {
      // BONUS deal with percentage reward - compute dollar value
      const bonusDollarValue = effectiveSpend * (pocDeal.reward_value / 100);
      headline = `Buy ${formatSpend(effectiveSpend)} ${pocDeal.gift_card_brand}, get ${formatValue(bonusDollarValue)} bonus${rewardFormSuffix} (${pocDeal.reward_value}% back)`;
      // Update computedDollarValue for this case
      computedDollarValue = bonusDollarValue;
    } else if (pocDeal.buy_amount && pocDeal.reward_value && !isPercent) {
      // BONUS deal with fixed dollar reward
      headline = `Buy $${pocDeal.buy_amount.toLocaleString()} ${pocDeal.gift_card_brand}, get $${pocDeal.reward_value}${rewardFormSuffix || " bonus"}`;
    } else if (pocDeal.reward_value && !isPercent) {
      headline = `Get $${pocDeal.reward_value}${rewardFormSuffix || ` bonus with ${pocDeal.gift_card_brand}`}`;
    } else if (pocDeal.evidence_verbatim_text) {
      headline = pocDeal.evidence_verbatim_text;
    } else {
      headline = `${pocDeal.gift_card_brand} bonus deal`;
    }
  } else if (pocDeal.evidence_verbatim_text || pocDeal.terms_text) {
    headline = pocDeal.evidence_verbatim_text || pocDeal.terms_text || "";
  } else {
    if (category === "POINTS") {
      headline = `Earn ${pocDeal.reward_value?.toLocaleString()} points on ${pocDeal.gift_card_brand}`;
    } else {
      headline = `${pocDeal.gift_card_brand} deal`;
    }
  }
  
  // Build decision reasons for transparency - CR059: Include headline mode debug info
  const decisionReasons: string[] = [];
  if (pocDeal.deal_type) {
    decisionReasons.push(`Deal type: ${pocDeal.deal_type}`);
  }
  // CR059: Debug info for headline mode
  decisionReasons.push(`Headline mode: ${headlineMode}`);
  if (pocDeal.partner_merchant) {
    decisionReasons.push(`Partner: ${pocDeal.partner_merchant}`);
  }
  if (pocDeal.buy_amount) {
    decisionReasons.push(`Buy amount: $${pocDeal.buy_amount}`);
  }
  if (effectiveSpend) {
    decisionReasons.push(`Qualifying spend: $${effectiveSpend.toLocaleString()}`);
  }
  if (discountPercentValue) {
    decisionReasons.push(`Discount: ${discountPercentValue}% back`);
  } else if (pocDeal.reward_value) {
    decisionReasons.push(`Reward: ${pocDeal.reward_value}${isPercent ? '% back' : isPoints ? ' points' : ' CAD'}`);
  }
  if (computedDollarValue) {
    decisionReasons.push(`Computed $ value: $${computedDollarValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (pocDeal.max_reward_cad) {
    decisionReasons.push(`Max reward: up to $${pocDeal.max_reward_cad}`);
  }
  if (pocDeal.eligibility_summary) {
    decisionReasons.push(`How to earn: ${pocDeal.eligibility_summary}`);
  }
  if (pocDeal.confidence) {
    decisionReasons.push(`AI confidence: ${Math.round(pocDeal.confidence * 100)}%`);
  }
  if (pocDeal.evidence_page) {
    decisionReasons.push(`Found on page ${pocDeal.evidence_page}`);
  }
  
  // CR064: Use deal_url for View deal button (offer URL), fall back to source_url
  const proofUrls: string[] = [];
  if (pocDeal.deal_url) {
    proofUrls.push(pocDeal.deal_url);
  } else if (pocDeal.source_url) {
    proofUrls.push(pocDeal.source_url);
  }
  
  // CR064: terms_url for T&Cs button (terms/conditions URL)
  const termsUrlValue = pocDeal.terms_url || null;
  
  // Discovery URL for audit - where the deal was found (kept for backward compat)
  // Falls back to the deal's source_url if no separate discovery URL exists
  const discoverySourceUrl = pocDeal.discovery_url || pocDeal.source_url;
  
  // Build fine print from terms + eligibility
  const finePrintParts: string[] = [];
  if (pocDeal.terms_text) finePrintParts.push(pocDeal.terms_text);
  if (pocDeal.eligibility_summary && !pocDeal.terms_text?.includes(pocDeal.eligibility_summary)) {
    finePrintParts.push(`How to earn: ${pocDeal.eligibility_summary}`);
  }
  if (pocDeal.max_reward_cad) {
    finePrintParts.push(`Maximum reward: up to $${pocDeal.max_reward_cad}`);
  }
  
  // Compute audience_hint based on spend threshold ($500+ = corporate)
  const audienceHint: "corporate_bulk" | "personal" | "unknown" = 
    effectiveSpend && effectiveSpend >= 500 ? "corporate_bulk" : "unknown";
  
  return {
    merchant: displayMerchant,
    deal_category: category,
    headline_mechanic: headline,
    purchase_gift_card_brand: pocDeal.gift_card_brand,
    purchase_brand_logo_url: null,
    bonus_merchant: category === "BONUS" ? pocDeal.gift_card_brand : null,
    bonus_merchant_logo_url: null,
    bonus_amount: category === "BONUS" && !isPercent ? pocDeal.reward_value : null,
    spend_amount: effectiveSpend,
    qualifying_spend_cad: effectiveSpend,
    computed_value_cad: computedDollarValue,
    // CR059: Set effective_percent_back for starburst badge display
    effective_percent_back: discountPercentValue ?? (category === "BONUS" && effectiveSpend && computedDollarValue 
      ? (computedDollarValue / effectiveSpend) * 100 
      : null),
    gift_card_brand: pocDeal.gift_card_brand,
    bonus_or_discount_amount: discountPercentValue ?? pocDeal.reward_value,
    // CR059: Set bonus_value_cad for BONUS deals
    bonus_value_cad: category === "BONUS" ? (computedDollarValue ?? (isPercent ? null : pocDeal.reward_value)) : null,
    deal_type: dealType,
    discount_percent: discountPercentValue,  // CR058: Use direct value from database
    discount_amount_cad: category === "DISCOUNT" && !discountPercentValue ? pocDeal.reward_value : null,
    points_amount: isPoints ? pocDeal.reward_value : null,
    points_program_name: isPoints ? pocDeal.gift_card_brand : null,
    spend_range_min: null,
    spend_range_max: null,
    discount_tiers: null,
    deal_pricing_type: null,
    start_date: pocDeal.start_date,
    end_date: pocDeal.end_date,
    bonus_valid_from: null,
    bonus_valid_until: null,
    requires_physical_card: null,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
    fine_print: finePrintParts.join(" • "),
    source_type: "website",
    source_url: discoverySourceUrl, // Where the deal was discovered (e.g., Walmart partner offers page)
    deal_url: pocDeal.deal_url || null,     // CR064: Direct link to view/claim the offer
    terms_url: termsUrlValue,               // CR064: Link to terms and conditions
    channel: "unknown",
    region: pocDeal.region || "Ontario",
    proof_urls: proofUrls, // The deal's direct URL (e.g., Fig's site)
    confidence: pocDeal.confidence >= 0.8 ? "High" : pocDeal.confidence >= 0.6 ? "Medium" : "Low",
    confidence_reason: `AI confidence: ${Math.round(pocDeal.confidence * 100)}%`,
    raw_source_notes: pocDeal.evidence_verbatim_text || "",
    evidence_snippet: pocDeal.evidence_verbatim_text || pocDeal.terms_text || null,
    decision_reasons: decisionReasons,
    is_active: true,
    audience_hint: audienceHint,
  };
}

// CR127: Using shared STATUS_COLORS from runStatusUtils (imported above)

// CR127: Using shared useRunSummary hook for consistent status/totals

export default function PocRuns() {
  const { isAdmin } = useAuth();
  const [runs, setRuns] = useState<PocRun[]>([]);
  const [promptPacks, setPromptPacks] = useState<Record<string, PromptPack>>({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [canceling, setCanceling] = useState(false);
  // CR128: Removed selectedRun, runMerchants, showDetailsDialog - Eye modal removed
  
  // Deals state
  const [pocDeals, setPocDeals] = useState<PocDeal[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [selectedRunForDeals, setSelectedRunForDeals] = useState<string | null>(null);
  const [selectedDealTypes, setSelectedDealTypes] = useState<DealCategory[] | "All">("All");
  // CR161: Origin filter state
  const [originFilter, setOriginFilter] = useState<OriginFilter>("all");

  const fetchRuns = async () => {
    try {
      // CR075: Filter out hidden rows (batch_s2_spam_run cleanup)
      const { data: runsData, error: runsError } = await supabase
        .from("poc_runs")
        .select("*")
        .eq("hidden_from_list", false)
        .order("created_at", { ascending: false })
        .limit(50);

      if (runsError) throw runsError;
      
      // CR161: Enrich runs with scheduler context from cr075_batch_runs
      const batchRunIds = (runsData || [])
        .filter(r => r.origin_type === "BATCH_RUN" && r.origin_id)
        .map(r => r.origin_id);
      
      const schedulerContextMap: Record<string, { schedule_run_id: string | null; schedule_id: string | null }> = {};
      
      if (batchRunIds.length > 0) {
        const { data: batchRuns } = await supabase
          .from("cr075_batch_runs")
          .select("id, schedule_run_id, schedule_id")
          .in("id", batchRunIds);
        
        if (batchRuns) {
          batchRuns.forEach(br => {
            schedulerContextMap[br.id] = {
              schedule_run_id: br.schedule_run_id,
              schedule_id: br.schedule_id
            };
          });
        }
      }
      
      // Merge scheduler context into runs
      const enrichedRuns = (runsData || []).map(run => {
        if (run.origin_type === "BATCH_RUN" && run.origin_id && schedulerContextMap[run.origin_id]) {
          return {
            ...run,
            schedule_run_id: schedulerContextMap[run.origin_id].schedule_run_id,
            schedule_id: schedulerContextMap[run.origin_id].schedule_id
          };
        }
        return run;
      });
      
      setRuns(enrichedRuns);

      // Fetch prompt packs for display
      const { data: packsData } = await supabase
        .from("prompt_packs")
        .select("id, name, version");

      if (packsData) {
        const packsMap: Record<string, PromptPack> = {};
        packsData.forEach((p) => {
          packsMap[p.id] = p;
        });
        setPromptPacks(packsMap);
      }
    } catch (err) {
      console.error("Error fetching POC runs:", err);
      toast.error("Failed to load runs");
    } finally {
      setLoading(false);
    }
  };

  // CR128: fetchRunMerchants removed - Eye modal removed, use Open page instead

  const fetchDeals = async (runId?: string) => {
    setLoadingDeals(true);
    try {
      let query = supabase
        .from("poc_deals")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (runId) {
        query = query.eq("run_id", runId);
      }
      
      const { data: dealsData, error } = await query.limit(100);
      
      if (error) throw error;
      
      // Fetch discovery URLs from poc_run_merchants for these deals
      const dealsList = dealsData || [];
      if (dealsList.length > 0) {
        // Get unique run_id + merchant_id combinations
        const merchantKeys = [...new Set(dealsList.map(d => `${d.run_id}|${d.merchant_id}`))];
        const runIds = [...new Set(dealsList.map(d => d.run_id))];
        
        const { data: merchantsData } = await supabase
          .from("poc_run_merchants")
          .select("poc_run_id, merchant_id, source_url")
          .in("poc_run_id", runIds);
        
        // Build lookup map for discovery URLs
        const discoveryUrlMap = new Map<string, string>();
        (merchantsData || []).forEach(m => {
          if (m.source_url) {
            discoveryUrlMap.set(`${m.poc_run_id}|${m.merchant_id}`, m.source_url);
          }
        });
        
        // Enrich deals with discovery_url
        const enrichedDeals = dealsList.map(deal => ({
          ...deal,
          discovery_url: discoveryUrlMap.get(`${deal.run_id}|${deal.merchant_id}`) || null
        }));
        
        setPocDeals(enrichedDeals as PocDeal[]);
      } else {
        setPocDeals([]);
      }
    } catch (err) {
      console.error("Error fetching POC deals:", err);
      toast.error("Failed to load deals");
    } finally {
      setLoadingDeals(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchRuns();
      fetchDeals(selectedRunForDeals || undefined);

      // Subscribe to real-time updates
      const channel = supabase
        .channel("poc-runs-updates")
        .on("postgres_changes", { event: "*", schema: "public", table: "poc_runs" }, () => {
          fetchRuns();
        })
        // CR128: poc_run_merchants subscription removed - no longer needed without Eye modal
        .on("postgres_changes", { event: "*", schema: "public", table: "poc_deals" }, () => {
          fetchDeals(selectedRunForDeals || undefined);
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isAdmin, selectedRunForDeals]);

  const handleStartRun = async (runMode: "full" | "merchant_only" = "full", merchantIds?: string[]) => {
    setStarting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in");
        return;
      }

      const body: { action: string; run_mode: string; scan_type: string; merchant_ids?: string[] } = { 
        action: "start", 
        run_mode: runMode,
        scan_type: "WEB", // CR388: Always pass scan_type for WEB pipeline
      };
      
      if (merchantIds && merchantIds.length > 0) {
        body.merchant_ids = merchantIds;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/poc-scan-run`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to start run");
      }

      toast.success(`POC run started (${runMode} mode)`);
      fetchRuns();
    } catch (err: any) {
      console.error("Error starting run:", err);
      toast.error(err.message || "Failed to start run");
    } finally {
      setStarting(false);
    }
  };

  const handleCancelRun = async (runId: string) => {
    setCanceling(true);
    try {
      const { error } = await supabase
        .from("poc_runs")
        .update({ status: "canceled", finished_at: new Date().toISOString() })
        .eq("id", runId)
        .eq("status", "running");

      if (error) throw error;

      toast.success("Run canceled");
      fetchRuns();
    } catch (err) {
      console.error("Error canceling run:", err);
      toast.error("Failed to cancel run");
    } finally {
      setCanceling(false);
    }
  };

  // CR128: handleViewDetails and getProgressStats removed - Eye modal removed

  const getRunningRun = () => runs.find((r) => r.status === "running");

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start) return "-";
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const seconds = Math.round((endDate.getTime() - startDate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  const runningRun = getRunningRun();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Website Runs</h1>
            <p className="text-muted-foreground">
              Run and monitor gift card deal scanning across merchants
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/scan-configuration">
                <Settings className="mr-2 h-4 w-4" />
                Test Settings
              </Link>
            </Button>
            <Button variant="outline" onClick={fetchRuns} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {runningRun ? (
              <Button
                variant="destructive"
                onClick={() => handleCancelRun(runningRun.id)}
                disabled={canceling}
              >
                {canceling ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Cancel Run
              </Button>
            ) : (
              <Button onClick={() => handleStartRun("full")} disabled={starting}>
                {starting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run All
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            {/* CR161: Origin filter pills */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filter:</span>
              <div className="flex gap-1">
                {(["all", "manual", "scheduler"] as OriginFilter[]).map((filter) => (
                  <Button
                    key={filter}
                    variant={originFilter === filter ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOriginFilter(filter)}
                    className="h-7 px-3 text-xs"
                  >
                    {filter === "all" ? "All" : filter === "manual" ? "Manual" : "Scheduler"}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : runs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No runs yet. Click "Run All" to start your first scan.
              </div>
            ) : (() => {
              // CR161: Apply origin filter
              const filteredRuns = runs
                .filter(run => !run.parent_run_id) // Only show parent runs (not child units)
                .filter(run => {
                  if (originFilter === "all") return true;
                  if (originFilter === "scheduler") return !!run.schedule_run_id;
                  if (originFilter === "manual") return !run.schedule_run_id;
                  return true;
                });
              
              if (filteredRuns.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    No runs match the selected filter.
                  </div>
                );
              }
              
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Origin</TableHead>
                      <TableHead>Schedule Run</TableHead>
                      <TableHead>Merchants</TableHead>
                      <TableHead>Staged</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRuns.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>
                          <Badge className={STATUS_COLORS[run.status] || "bg-gray-500"}>
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* CR161: Distinguish between Manual, Batch Run, and Scheduler */}
                          {run.schedule_run_id ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 gap-1">
                              <Calendar className="h-3 w-3" />
                              Scheduler
                            </Badge>
                          ) : run.origin_type === "BATCH_RUN" ? (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                              <Link to={`/admin/batch-runs/${run.origin_id}`} className="hover:underline">
                                Batch Run
                              </Link>
                            </Badge>
                          ) : (
                            <Badge variant="outline">Website UI</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* CR161: Schedule Run link */}
                          {run.schedule_run_id ? (
                            <Link 
                              to={`/admin/scheduler/runs/${run.schedule_run_id}`}
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                            >
                              {run.schedule_run_id.slice(0, 8)}...
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {run.origin_type === "BATCH_RUN" ? (
                            <span className="text-sm">
                              {run.units_succeeded}/{run.units_total}
                              {run.units_failed > 0 && (
                                <span className="text-red-500 ml-1">({run.units_failed} failed)</span>
                              )}
                            </span>
                          ) : (
                            run.merchant_limit_used
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={run.staged_count > 0 ? "text-green-600 font-medium" : ""}>
                            {run.staged_count || 0}
                          </span>
                        </TableCell>
                        <TableCell>{formatDuration(run.started_at, run.finished_at)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(run.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {/* CR161: Link to batch run if scheduler-origin */}
                          {run.origin_type === "BATCH_RUN" && run.origin_id && (
                            <Button variant="ghost" size="icon" asChild title="View Batch Run">
                              <Link to={`/admin/batch-runs/${run.origin_id}`}>
                                <Play className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {/* CR128: Eye icon removed - use Open (detail page) as canonical view */}
                          <Button variant="ghost" size="icon" asChild title="Open run details">
                            <Link to={`/admin/poc-runs/${run.id}`}>
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </CardContent>
        </Card>

        {/* Deals Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" />
                <CardTitle>Found Deals</CardTitle>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  className="text-sm border rounded px-2 py-1 bg-background"
                  value={selectedRunForDeals || ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setSelectedRunForDeals(val);
                    fetchDeals(val || undefined);
                  }}
                >
                  <option value="">All runs</option>
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {new Date(r.created_at).toLocaleString()} ({r.status})
                    </option>
                  ))}
                </select>
                <Badge variant="secondary">{pocDeals.length} deals</Badge>
              </div>
            </div>
          <CardDescription>
              Gift card deals extracted by AI scans, displayed in the standard deal card format
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Deal Type Filter */}
            <DealTypeFilter
              selectedTypes={selectedDealTypes}
              onSelectionChange={setSelectedDealTypes}
              className="mb-6"
            />
            
            {loadingDeals ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : pocDeals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No deals found. Run a POC scan to extract deals.
              </div>
            ) : (() => {
              // Filter deals by selected types
              const filteredDeals = selectedDealTypes === "All" 
                ? pocDeals 
                : pocDeals.filter(pocDeal => {
                    const { category } = mapPocDealType(pocDeal.deal_type);
                    return (selectedDealTypes as DealCategory[]).includes(category);
                  });
              
              return filteredDeals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No deals match the selected type filter.
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {filteredDeals.map((pocDeal) => (
                  <div key={pocDeal.id} className="space-y-3">
                    <DealGiftCard 
                      deal={mapPocDealToUIDeal(pocDeal)} 
                      isAdmin={true}
                    />
                    {/* Screenshot Evidence */}
                    {pocDeal.screenshot_url && (
                      <div className="rounded-lg border bg-muted/30 p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                          <ImageIcon className="h-3.5 w-3.5" />
                          Screenshot Evidence
                        </p>
                        <a 
                          href={pocDeal.screenshot_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img 
                            src={pocDeal.screenshot_url} 
                            alt={`Screenshot of ${pocDeal.merchant_name || 'source'}`}
                            className="w-full rounded border shadow-sm hover:opacity-90 transition-opacity cursor-zoom-in max-h-[300px] object-cover object-top"
                          />
                        </a>
                        <p className="text-[10px] text-muted-foreground mt-1.5 truncate">
                          {pocDeal.source_url}
                        </p>
                      </div>
                    )}
                  </div>
                    ))}
                  </div>
                );
              })()
            }
          </CardContent>
        </Card>

        {/* CR128: Run Details Dialog removed - use Open page (detail page) as canonical view */}
      </div>
    </AdminLayout>
  );
}
